import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import DataCard from '../ui/DataCard';

/**
 * DashboardSessionsTab - Sessions Tab im Dashboard
 * 
 * Zeigt:
 * - Aktive Sessions (hervorgehoben)
 * - Beendete Sessions
 * - Filter nach Status
 */
export default function DashboardSessionsTab({
  instances,
  activeInstances,
  onEndSession,
  onDeleteSession
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('active'); // 'active' | 'ended' | 'all'

  // Sort and filter sessions
  const endedSessions = useMemo(() => {
    return instances
      .filter(i => !i.isActive)
      .sort((a, b) => (b.endedAt || b.createdAt) - (a.endedAt || a.createdAt));
  }, [instances]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = Math.floor((end - start) / 1000);
    
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    
    if (minutes < 60) return `${minutes}:${seconds.toString().padStart(2, '0')} Min`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
            filter === 'active'
              ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          Aktiv ({activeInstances.length})
        </button>
        <button
          onClick={() => setFilter('ended')}
          className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
            filter === 'ended'
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          Beendet ({endedSessions.length})
        </button>
      </div>

      {/* Active Sessions */}
      {filter === 'active' && (
        <div className="space-y-3">
          {activeInstances.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-5xl mb-4 block">▶️</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Keine aktiven Sessions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Starte ein Quiz, um eine Session zu beginnen
              </p>
            </div>
          ) : (
            activeInstances.map((session) => (
              <DataCard
                key={session.id}
                variant="compact"
                color="#EF4444"
                title={session.templateName || 'Quiz Session'}
                subtitle={`Code: ${session.sessionCode} • ${session.participants?.length || 0} Teilnehmer • ${formatDuration(session.startedAt)}`}
                badges={[
                  { label: 'Live', variant: 'danger', pulse: true }
                ]}
                menuActions={[
                  { label: 'Live öffnen', onClick: () => navigate(`/live/${session.id}`) },
                  { label: 'Ergebnisse', onClick: () => navigate(`/results/${session.id}`) },
                  { label: 'Beenden', variant: 'warning', onClick: () => onEndSession(session.id) },
                  { label: 'Löschen', variant: 'danger', onClick: () => onDeleteSession(session.id) }
                ]}
                onClick={() => navigate(`/live/${session.id}`)}
              />
            ))
          )}
        </div>
      )}

      {/* Ended Sessions */}
      {filter === 'ended' && (
        <div className="space-y-3">
          {endedSessions.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-5xl mb-4 block">📊</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Keine beendeten Sessions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Beendete Quiz-Sessions werden hier angezeigt
              </p>
            </div>
          ) : (
            endedSessions.map((session) => (
              <DataCard
                key={session.id}
                variant="compact"
                title={session.templateName || 'Quiz Session'}
                subtitle={formatDate(session.endedAt || session.createdAt)}
                stats={[
                  { value: session.participants?.length || 0, label: 'Teiln.' },
                  { value: session.averageScore ? `${Math.round(session.averageScore)}%` : 'N/A', label: 'Score' }
                ]}
                menuActions={[
                  { label: 'Ergebnisse ansehen', onClick: () => navigate(`/results/${session.id}`) },
                  { label: 'Als CSV exportieren', onClick: () => alert('Export wird noch implementiert') },
                  { label: 'Löschen', variant: 'danger', onClick: () => onDeleteSession(session.id) }
                ]}
                onClick={() => navigate(`/results/${session.id}`)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

DashboardSessionsTab.propTypes = {
  instances: PropTypes.array.isRequired,
  activeInstances: PropTypes.array.isRequired,
  onEndSession: PropTypes.func.isRequired,
  onDeleteSession: PropTypes.func.isRequired
};
